import type { LegalContent, Locale } from "./types";

export const termsByLocale: Record<Locale, LegalContent> = {
  en: {
    title: "Terms of Use",
    lastUpdatedLabel: "Last updated",
    lastUpdated: "2026-09-22",
    sections: [
      {
        heading: "Acceptance",
        body: [
          "By using Moodshot you agree to these terms. If you do not agree, please do not use the app."
        ]
      },
      {
        heading: "Licence",
        body: [
          "You are granted a personal, non-transferable licence to use Moodshot on devices you own, under Apple's standard End User Licence Agreement. You may not resell it, or attempt to decompile or reverse-engineer it, except where that right cannot be excluded by law."
        ]
      },
      {
        heading: "Purchases and refunds",
        body: [
          "The app is free to download and offers one non-consumable in-app purchase, “Deluxe”. The price shown on the App Store product page applies; billing and refunds are handled entirely by Apple under Apple's terms. Full-track playback requires an Apple Music subscription purchased separately from Apple; billing, renewal and refunds for that subscription are likewise handled entirely by Apple. Deluxe does not include music."
        ]
      },
      {
        heading: "Your content stays yours",
        body: [
          "Photos you take and moments you save remain yours. They are never uploaded to our servers. iCloud sync is on by default, and while it is on they also live in your own private iCloud space, where we still have no access to them and no ability to retrieve them. You can turn sync off and remove that iCloud copy at any time in Settings."
        ]
      },
      {
        heading: "Disclaimers",
        body: [
          "Moodshot is provided “as is”. Mood recognition is an automated guess based on image content and the time of day, and it will sometimes be wrong. It is meant as a way to find something to listen to, not as an assessment of anything about you. Playlist availability, contents and playback depend on Apple Music and on your region and subscription, and may change without notice."
        ]
      },
      {
        heading: "Limitation of liability",
        body: [
          "To the extent permitted by law, we are not liable for indirect or incidental losses arising from use of the app, including loss of saved moments caused by device failure, deletion of the app, or a backup you did not keep."
        ]
      },
      {
        heading: "Changes",
        body: [
          "These terms may be updated; the current version is always the one posted here."
        ]
      },
      {
        heading: "Contact",
        body: [
          "axqora@gmail.com"
        ]
      }
    ]
  },
  zh: {
    title: "用户协议",
    lastUpdatedLabel: "最后更新",
    lastUpdated: "2026-09-22",
    sections: [
      {
        heading: "接受条款",
        body: [
          "使用 Moodshot 即表示你接受本条款。如果你不接受，请不要使用本 App。"
        ]
      },
      {
        heading: "许可",
        body: [
          "在 Apple 标准最终用户许可协议（EULA）约束下，你获得在自有设备上使用 Moodshot 的个人、非排他且不可转让许可。除法律明确赋予且不可排除的权利外，你不得转售、反编译或逆向工程本 App。"
        ]
      },
      {
        heading: "付费与退款",
        body: [
          "本 App 支持免费下载，并提供一项一次性买断的「典藏版」内购项目。价格以 App Store 商品页展示为准，扣款与退款完全由 Apple 依据其平台条款处理。收听完整曲目需另行向 Apple 开通 Apple Music 订阅；该订阅的计费、续订和退款同样完全由 Apple 处理。典藏版不包含音乐。"
        ]
      },
      {
        heading: "你的内容仍然属于你",
        body: [
          "你拍下的照片和收藏的瞬间完全归你所有，且从不会上传到我们的服务器。iCloud 同步默认开启，开启期间它们会存在你自己的 iCloud 私有空间里，我们依然无法接触这些内容，亦无法协助取回。你可以随时在设置里关闭同步并移除 iCloud 里的这份副本。"
        ]
      },
      {
        heading: "免责声明",
        body: [
          "Moodshot 按「现状」提供服务。氛围识别系基于画面光影、场景与拍摄时段的自动化推断，可能存在偏差。该功能旨在根据你身处的环境为你提供契合心境的音乐灵感，并非对你个人的心理状态或特征作出的任何医学或心理学评估。歌单的可用性、曲目内容与播放权限取决于 Apple Music 平台以及你所在的地区与订阅状态，可能随时调整。"
        ]
      },
      {
        heading: "责任限制",
        body: [
          "在法律允许的最大范围内，我们不对因使用本 App 导致的任何间接或附带损失承担责任，包括因设备故障、卸载 App 或未保留备份而导致的本地收藏丢失。"
        ]
      },
      {
        heading: "变更",
        body: [
          "本条款可能会进行修订，最新版本将始终以此页面发布的内容为准。"
        ]
      },
      {
        heading: "联系",
        body: [
          "axqora@gmail.com"
        ]
      }
    ]
  },
  "zh-Hant": {
    title: "使用條款",
    lastUpdatedLabel: "最後更新",
    lastUpdated: "2026-09-22",
    sections: [
      {
        heading: "接受條款",
        body: [
          "使用 Moodshot 即表示你接受本條款。如果你不接受，請不要使用本 App。"
        ]
      },
      {
        heading: "授權",
        body: [
          "在 Apple 標準最終使用者授權合約（EULA）約束下，你取得在自有裝置上使用 Moodshot 的個人、非排他且不可轉讓授權。除法律明確賦予且不可排除的權利外，你不得轉售、反編譯或逆向工程本 App。"
        ]
      },
      {
        heading: "付費與退款",
        body: [
          "本 App 支援免費下載，並提供一項一次付清買斷的「典藏版」App 內購買項目。價格以 App Store 商品頁面顯示為準，扣款與退款完全由 Apple 依其平台條款處理。收聽完整曲目需另行向 Apple 訂閱 Apple Music；該訂閱的計費、續訂與退款同樣完全由 Apple 處理。典藏版不包含音樂。"
        ]
      },
      {
        heading: "你的內容仍然屬於你",
        body: [
          "你拍下的照片和收藏的瞬間完全歸你所有，且從不會上傳到我們的伺服器。iCloud 同步預設開啟，開啟期間它們會存在你自己的 iCloud 私有空間裡，我們依然無法接觸這些內容，亦無法協助取回。你可以隨時在設定裡關閉同步並移除 iCloud 裡的這份副本。"
        ]
      },
      {
        heading: "免責聲明",
        body: [
          "Moodshot 依「現狀」提供服務。氛圍辨識是基於畫面光影、場景與拍攝時段的自動化推斷，可能存在偏差。該功能旨在依你身處的環境為你提供契合心境的音樂靈感，並非對你個人的心理狀態或特徵作出的任何醫學或心理學評估。播放列表的可用性、曲目內容與播放權限取決於 Apple Music 平台以及你所在的地區與訂閱狀態，可能隨時調整。"
        ]
      },
      {
        heading: "責任限制",
        body: [
          "在法律允許的最大範圍內，我們不對因使用本 App 導致的任何間接或附帶損失承擔責任，包括因裝置故障、解除安裝 App 或未保留備份而導致的本機收藏遺失。"
        ]
      },
      {
        heading: "變更",
        body: [
          "本條款可能會進行修訂，最新版本將始終以此頁面發布的內容為準。"
        ]
      },
      {
        heading: "聯絡方式",
        body: [
          "axqora@gmail.com"
        ]
      }
    ]
  }
};
